/**
 * UploadedFileThumbnail — Miniature d'une pièce jointe.
 *
 * Spec rev150 (2026-02-07) — les couleurs et l'icône sont désormais
 * dictées PAR LE TYPE DE FICHIER, plus par le module hôte. Chaque
 * badge est un carré (défaut 36×36, border-radius 8) coloré selon
 * l'extension du fichier via le helper `getFileTypeMeta` :
 *   • PDF    : rouge (#e07a7a sur #3a1414)
 *   • XLS/CSV: vert (#5ab87a sur #0f2818)
 *   • DOC    : bleu (#5aa8e0 sur #101e2e)
 *   • Image  : violet (#c99aeb sur #2a1a3a) — ou vraie miniature si
 *              `imageUrl` est fourni
 *   • ZIP    : orange (#e0a55a sur #2a2410)
 *   • Autre  : gris (#8b8b90 sur #232327)
 *
 * Le prop `accent` reste accepté (rétrocompat) mais seulement utilisé
 * pour le halo `isNew` (indicateur d'upload fraîchement terminé).
 *
 * Props :
 *   - filename : nom du fichier (détermine icône + couleurs).
 *   - accent   : couleur du module hôte (facultative — halo isNew).
 *   - isNew    : badge Check vert (upload fraîchement terminé).
 *   - size     : taille en px (défaut 36).
 *   - imageUrl : si fourni + fichier de type image, affiche la vraie
 *                miniature à la place de l'icône.
 *   - testid   : suffixe testid optionnel.
 */
import { Check } from "lucide-react";
import { getFileTypeMeta } from "@/lib/fileTypeMeta";

export default function UploadedFileThumbnail({
  filename = "",
  accent = "#2B579A",
  isNew = false,
  size = 36,
  imageUrl,
  testid,
}) {
  const { Icon, iconColor, bg, isImage } = getFileTypeMeta(filename);
  const iconSize = Math.max(14, Math.round(size * 0.5));
  const showRealImage = isImage && !!imageUrl;

  return (
    <div
      className="shrink-0 overflow-visible flex items-center justify-center relative"
      style={{
        width: size,
        height: size,
        background: bg,
        border: `1px solid ${bg}`, // même teinte pour ne pas surcharger
        borderRadius: 8,
        color: iconColor,
        boxShadow: isNew ? `0 0 12px ${accent}33` : undefined,
      }}
      title={filename}
      data-testid={testid}
    >
      {showRealImage ? (
        <img
          src={imageUrl}
          alt={filename || ""}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: 7, // 1px inner offset pour montrer le bord
          }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <Icon size={iconSize} strokeWidth={2} />
      )}
      {isNew && (
        <span
          className="absolute -bottom-1 -right-1 rounded-full flex items-center justify-center"
          style={{
            width: 18, height: 18,
            background: "#22C55E",
            color: "#0B0B0C",
            border: "2px solid #1B1B1B",
            boxShadow: "0 0 8px rgba(34,197,94,0.55)",
          }}
          aria-label="Fichier venant d'être ajouté"
        >
          <Check size={11} strokeWidth={3} />
        </span>
      )}
    </div>
  );
}
